import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import { brand } from '../theme';
import BrandMark from './ui/BrandMark';
import BetaBadge from './ui/BetaBadge';
import { performLogout } from '../utils/logout';
import { EDITOR_HOME_PATH, isEditorOnlyRole } from '../utils/editorAccount';
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import VideoSettingsOutlinedIcon from '@mui/icons-material/VideoSettingsOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';

interface NavItem {
  text: string;
  path: string;
  /** Path prefixes that belong to this item, so detail pages keep it highlighted. */
  match: string[];
}

// Work, in the order the workflow runs. Account pages (profile, billing) and
// help live in the menus on the right, not in this bar.
const CREATOR_NAV: NavItem[] = [
  { text: 'Dashboard', path: '/dashboard', match: ['/dashboard'] },
  { text: 'Projects', path: '/my-projects', match: ['/my-projects', '/project-details', '/submit-editing-request'] },
  { text: 'Library', path: '/library', match: ['/library', '/upload', '/annotate', '/annotate-multicam', '/multicam'] },
  { text: 'Editors', path: '/my-editors', match: ['/my-editors'] },
  { text: 'Insights', path: '/insights', match: ['/insights'] },
];

// Editing work for accounts that are both creator and editor.
const EDITING_NAV: NavItem = { text: 'Editing', path: EDITOR_HOME_PATH, match: ['/edit-projects'] };

// An editor-only account has no library, projects, editors or subscription of
// its own - the router keeps it off those pages - so its bar is just its work.
const EDITOR_ONLY_NAV: NavItem[] = [
  { text: 'Projects', path: EDITOR_HOME_PATH, match: ['/edit-projects'] },
];

const isPathIn = (pathname: string, prefixes: string[]) =>
  prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

const roleIncludes = (role: string | undefined | null, part: string) =>
  (role || '').split(',').map((r) => r.trim()).includes(part);

const roleLabel = (role?: string | null): string => {
  if (roleIncludes(role, 'superadmin')) return 'Superadmin';
  if (isEditorOnlyRole(role)) return 'Editor';
  if (roleIncludes(role, 'editor')) return 'Creator and editor';
  return 'Creator';
};

const initialsFor = (name?: string | null, email?: string | null): string => {
  const source = (name || '').trim();
  if (source) {
    const parts = source.split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  }
  return (email || '?').trim().charAt(0).toUpperCase() || '?';
};

const ON_DARK_TEXT = '#FFFDF8';
const ON_DARK_MUTED = 'rgba(248, 245, 239, 0.72)';
const ON_DARK_HOVER_BG = 'rgba(248, 245, 239, 0.08)';

const Header = () => {
  const navigate = useNavigate();
  const { user, roleLoading } = useAuth();
  const location = useLocation();
  const [navMenuAnchor, setNavMenuAnchor] = useState<null | HTMLElement>(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const [signOutFailed, setSignOutFailed] = useState(false);

  const handleSignOut = async () => {
    setAccountMenuAnchor(null);
    try {
      // Signs out every open tab and clears cached GraphQL results.
      await performLogout();
      // Replace, so Back doesn't land on the page the user just left.
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      // The user believes they signed out - say so if they did not.
      setSignOutFailed(true);
    }
  };

  if (location.pathname === '/login' || location.pathname === '/signup') {
    return null;
  }

  const role = user?.role;
  const isEditorOnly = isEditorOnlyRole(role);
  const isSuperadmin = roleIncludes(role, 'superadmin');
  const homePath = isEditorOnly ? EDITOR_HOME_PATH : '/dashboard';

  // Nothing until the role is known, rather than flashing the creator menu at
  // an editor.
  const navItems: NavItem[] = roleLoading
    ? []
    : isEditorOnly
      ? EDITOR_ONLY_NAV
      : roleIncludes(role, 'editor')
        ? [...CREATOR_NAV, EDITING_NAV]
        : CREATOR_NAV;

  const goTo = (path: string) => {
    setNavMenuAnchor(null);
    setAccountMenuAnchor(null);
    navigate(path);
  };

  const displayName = user?.displayName?.trim() || '';
  const accountMenuOpen = Boolean(accountMenuAnchor);

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: brand.ink,
          backgroundImage: 'none',
          borderBottom: '1px solid rgba(248, 245, 239, 0.12)',
          boxShadow: 'none',
          color: ON_DARK_TEXT,
        }}
      >
        <Toolbar
          sx={{
            width: '100%',
            maxWidth: 1600,
            mx: 'auto',
            minHeight: { xs: 60, sm: 64 },
            gap: { xs: 1, md: 3 },
            px: { xs: 1.5, sm: 3 },
          }}
        >
          <Link to={homePath} style={{ textDecoration: 'none' }} aria-label="PithPlay dashboard">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: 2,
                transition: 'background-color 0.18s ease',
                '&:hover': { backgroundColor: ON_DARK_HOVER_BG },
              }}
            >
              <BrandMark size={34} onDark />
              <Typography
                sx={{
                  color: ON_DARK_TEXT,
                  fontSize: '1.1rem',
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                }}
              >
                PithPlay
              </Typography>
              <BetaBadge onDark />
            </Box>
          </Link>

          {/* Main navigation (large screens) */}
          <Box
            component="nav"
            aria-label="Main"
            sx={{
              display: { xs: 'none', lg: 'flex' },
              alignItems: 'stretch',
              alignSelf: 'stretch',
              gap: 0.5,
              flex: 1,
              minWidth: 0,
            }}
          >
            {navItems.map((item) => {
              const active = isPathIn(location.pathname, item.match);
              return (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    color: active ? ON_DARK_TEXT : ON_DARK_MUTED,
                    borderRadius: 0,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 1.5,
                    minWidth: 0,
                    boxShadow: active ? `inset 0 -2px 0 ${brand.copperLight}` : 'none',
                    '&:hover': {
                      backgroundColor: ON_DARK_HOVER_BG,
                      color: ON_DARK_TEXT,
                    },
                  }}
                >
                  {item.text}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, ml: 'auto' }}>
            {/* Navigation menu (smaller screens) */}
            {navItems.length > 0 && (
              <IconButton
                onClick={(event) => setNavMenuAnchor(event.currentTarget)}
                aria-label="Open navigation"
                aria-haspopup="true"
                sx={{ display: { xs: 'inline-flex', lg: 'none' }, color: ON_DARK_TEXT }}
              >
                <MenuOutlinedIcon />
              </IconButton>
            )}

            <Tooltip title="Help">
              <IconButton
                component={Link}
                to="/how-to"
                aria-label="Help"
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  color: isPathIn(location.pathname, ['/how-to']) ? ON_DARK_TEXT : ON_DARK_MUTED,
                  '&:hover': { color: ON_DARK_TEXT, backgroundColor: ON_DARK_HOVER_BG },
                }}
              >
                <HelpOutlineOutlinedIcon />
              </IconButton>
            </Tooltip>

            {user && <NotificationBell />}

            {user && (
              <Tooltip title="Account">
                <IconButton
                  onClick={(event) => setAccountMenuAnchor(event.currentTarget)}
                  aria-label="Account menu"
                  aria-haspopup="true"
                  aria-controls={accountMenuOpen ? 'account-menu' : undefined}
                  aria-expanded={accountMenuOpen ? 'true' : undefined}
                  sx={{ p: 0.5 }}
                >
                  <Avatar
                    src={user.photoURL || undefined}
                    alt=""
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      bgcolor: 'rgba(248, 245, 239, 0.16)',
                      color: ON_DARK_TEXT,
                    }}
                  >
                    {initialsFor(displayName, user.email)}
                  </Avatar>
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Navigation menu contents (smaller screens) */}
          <Menu
            anchorEl={navMenuAnchor}
            open={Boolean(navMenuAnchor)}
            onClose={() => setNavMenuAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{ sx: { mt: 1, minWidth: 220 } }}
          >
            {navItems.map((item) => {
              const active = isPathIn(location.pathname, item.match);
              return (
                <MenuItem
                  key={item.text}
                  selected={active}
                  onClick={() => goTo(item.path)}
                  sx={{ fontWeight: active ? 700 : 500 }}
                >
                  {item.text}
                </MenuItem>
              );
            })}
            <Divider />
            <MenuItem onClick={() => goTo('/how-to')}>
              <ListItemIcon><HelpOutlineOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Help</ListItemText>
            </MenuItem>
          </Menu>

          {/* Account menu */}
          <Menu
            id="account-menu"
            anchorEl={accountMenuAnchor}
            open={accountMenuOpen}
            onClose={() => setAccountMenuAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{ sx: { mt: 1, minWidth: 240 } }}
          >
            <Box sx={{ px: 2, pt: 1, pb: 1.25 }}>
              {displayName && (
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
                  {displayName}
                </Typography>
              )}
              {user?.email && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {user.email}
                </Typography>
              )}
              {!roleLoading && (
                <Typography variant="caption" color="text.secondary" data-testid="account-role">
                  {roleLabel(role)}
                </Typography>
              )}
            </Box>
            <Divider />
            <MenuItem onClick={() => goTo('/profile')}>
              <ListItemIcon><PersonOutlineOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            {!roleLoading && !isEditorOnly && (
              <MenuItem onClick={() => goTo('/billing')}>
                <ListItemIcon><CreditCardOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Plan and billing</ListItemText>
              </MenuItem>
            )}
            {/* Every creator account keeps a way into editing work, even one
                whose role predates the creator,editor convention. */}
            {!roleLoading && !isEditorOnly && (
              <MenuItem onClick={() => goTo(EDITOR_HOME_PATH)}>
                <ListItemIcon><VideoSettingsOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Editing workspace</ListItemText>
              </MenuItem>
            )}
            {isSuperadmin && (
              <MenuItem onClick={() => goTo('/admin/security-monitoring')}>
                <ListItemIcon><SecurityOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Security monitoring</ListItemText>
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleSignOut}>
              <ListItemIcon><LogoutOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Sign out</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Snackbar
        open={signOutFailed}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSignOutFailed(false); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setSignOutFailed(false)}>
          We couldn&apos;t sign you out completely. Close this browser tab to finish signing out.
        </Alert>
      </Snackbar>
    </>
  );
};

export default Header;
